<script setup lang="ts">
import { computed, ref } from "vue";
import { formatGib } from "../format";

export interface DayBar {
  /** UTC calendar day, YYYY-MM-DD. */
  date: string;
  /** Null when vnStat has no record for the day; a missing day is not a zero. */
  total: number | null;
  rx: number | null;
  tx: number | null;
}

/**
 * Daily traffic as thin columns. Days before `emphasisFrom` are drawn lighter, which lets the
 * current billing cycle stand out from the tail of the previous one.
 */
const props = withDefaults(
  defineProps<{ days: ReadonlyArray<DayBar>; today: string; emphasisFrom?: string | null; height?: number }>(),
  { emphasisFrom: null, height: 96 }
);

const root = ref<HTMLElement | null>(null);
const active = ref<number | null>(null);

/** A tap has no hover to end it, so it takes focus and the readout stays until focus leaves. */
function onTap(event: PointerEvent, index: number) {
  active.value = index;
  if (event.pointerType !== "mouse") root.value?.focus({ preventScroll: true });
}

function onLeave(event: PointerEvent) {
  if (event.pointerType === "mouse") active.value = null;
}

const peak = computed(() => {
  let index = -1;
  props.days.forEach((day, position) => {
    if (day.total != null && (index < 0 || day.total > (props.days[index].total as number))) index = position;
  });
  return index;
});

const peakValue = computed(() => (peak.value < 0 ? 0 : (props.days[peak.value].total as number)));

const label = (date: string) => `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`;
const longLabel = (date: string) => `${Number(date.slice(5, 7))}月${Number(date.slice(8, 10))}日`;

const slots = computed(() => {
  const last = props.days.length - 1;
  return props.days.map((day, index) => ({
    ...day,
    height: day.total == null || peakValue.value <= 0 ? 0 : Math.max(1.5, (day.total / peakValue.value) * 100),
    muted: props.emphasisFrom != null && day.date < props.emphasisFrom,
    // A date every seventh column, counted back from the newest so "today" is always labelled.
    tick: (last - index) % 7 === 0 ? (day.date === props.today ? "今日" : label(day.date)) : "",
    edge: index < 2 ? "start" : index > last - 2 ? "end" : "middle"
  }));
});

const tip = computed(() => {
  const index = active.value;
  if (index == null || !props.days[index]) return null;
  const day = props.days[index];
  const position = ((index + 0.5) / props.days.length) * 100;
  return {
    day,
    title: `${longLabel(day.date)}${day.date === props.today ? " · 今日（进行中）" : ""}`,
    style: position > 50 ? { right: `${100 - position}%`, marginRight: "0.75rem" } : { left: `${position}%`, marginLeft: "0.75rem" }
  };
});

function onKey(event: KeyboardEvent) {
  const last = props.days.length - 1;
  if (last < 0) return;
  const current = active.value ?? last;
  if (event.key === "ArrowLeft") active.value = Math.max(0, current - 1);
  else if (event.key === "ArrowRight") active.value = Math.min(last, current + 1);
  else if (event.key === "Escape") active.value = null;
  else return;
  event.preventDefault();
}

const summary = computed(() => {
  const recorded = props.days.filter((day) => day.total != null).length;
  const top = peak.value < 0 ? "" : `，最高 ${longLabel(props.days[peak.value].date)} ${formatGib(peakValue.value)} GiB`;
  return `近 ${props.days.length} 天每日流量，共 ${recorded} 天有记录${top}。按左右方向键逐日读取。`;
});
</script>

<template>
  <figure
    ref="root"
    class="bars"
    role="img"
    tabindex="0"
    :aria-label="summary"
    @keydown="onKey"
    @blur="active = null"
  >
    <div class="bars__plot" :style="{ height: `${height}px` }" @pointerleave="onLeave">
      <div
        v-for="(slot, index) in slots"
        :key="slot.date"
        class="bars__slot"
        :class="{ 'bars__slot--active': active === index, 'bars__slot--muted': slot.muted }"
        @pointerenter="active = index"
        @pointerdown="onTap($event, index)"
      >
        <span v-if="index === peak && active == null" class="bars__peak num" :class="`bars__peak--${slot.edge}`">
          {{ formatGib(slot.total, 0) }} GiB
        </span>
        <i v-if="slot.total != null" :style="{ height: `${slot.height}%` }"></i>
      </div>
    </div>
    <div class="bars__axis" aria-hidden="true">
      <span v-for="slot in slots" :key="slot.date">
        <em v-if="slot.tick" :class="`bars__tick--${slot.edge}`">{{ slot.tick }}</em>
      </span>
    </div>

    <div v-if="tip" class="bars__tip" :style="tip.style">
      <p class="bars__tip-time">{{ tip.title }}</p>
      <template v-if="tip.day.total != null">
        <p><strong class="num">{{ formatGib(tip.day.total) }} GiB</strong></p>
        <p class="num">接收 {{ formatGib(tip.day.rx) }} · 发送 {{ formatGib(tip.day.tx) }}</p>
      </template>
      <p v-else>没有记录</p>
    </div>
  </figure>
</template>

<style scoped>
.bars { position: relative; min-width: 0; border-radius: var(--radius-small); touch-action: pan-y; }
.bars__plot { display: flex; align-items: flex-end; border-bottom: 1px solid var(--axis); }
.bars__slot {
  position: relative;
  display: flex;
  flex: 1 1 0;
  align-items: flex-end;
  justify-content: center;
  min-width: 0;
  height: 100%;
  /* Half of the gap on each side: the whole slot is the hover target, not just the column. */
  padding-inline: 1px;
}
.bars__slot i {
  display: block;
  width: 100%;
  max-width: 0.875rem;
  border-radius: 3px 3px 0 0;
  background: var(--accent);
}
.bars__slot--muted i { background: var(--accent-soft); }
.bars__slot--active { background: var(--wash); }
.bars__slot--active i { filter: brightness(1.12); }

.bars__peak {
  position: absolute;
  bottom: 100%;
  left: 50%;
  margin-bottom: 0.25rem;
  color: var(--ink-2);
  font-size: 0.75rem;
  white-space: nowrap;
  transform: translateX(-50%);
  pointer-events: none;
}
.bars__peak--start { left: 0; transform: none; }
.bars__peak--end { right: 0; left: auto; transform: none; }

.bars__axis { display: flex; height: 1.25rem; margin-top: 0.35rem; color: var(--ink-3); font-size: 0.71875rem; }
.bars__axis span { position: relative; flex: 1 1 0; min-width: 0; }
.bars__axis em {
  position: absolute;
  left: 50%;
  font-style: normal;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  transform: translateX(-50%);
}
.bars__axis .bars__tick--start { left: 0; transform: none; }
.bars__axis .bars__tick--end { right: 0; left: auto; transform: none; }

.bars__tip {
  position: absolute;
  top: 0;
  z-index: 2;
  display: grid;
  gap: 0.15rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--rule);
  border-radius: var(--radius-small);
  background: var(--surface);
  box-shadow: var(--shadow-pop);
  color: var(--ink-2);
  font-size: 0.8125rem;
  line-height: 1.35;
  white-space: nowrap;
  pointer-events: none;
}
.bars__tip-time { color: var(--ink-3); font-size: 0.75rem; }
.bars__tip strong { color: var(--ink); font-weight: 600; }
</style>
